import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../../CommonComponent/sidebar/Sidebar";
import TopNavbar from "../../CommonComponent/topNavbar/TopNavbar";
import { Footer } from "../../Footer";
import { Nav } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { MultiSelect } from "react-multi-select-component";
import * as icons from "react-bootstrap-icons";
import { GetSubScription } from "../../redux/auth-reducer/auth-service";
import { useNavigate } from "react-router-dom";
import {
  endUserInstructions,
  isSubscriptionActive as isServerSubscriptionActive,
  pickCurrentSubscription,
} from "../../utils/helper/helper";
import {
  AUCTIONS_PAGE_SIZE,
  AUCTION_TABS,
  AUCTION_TAB_LABELS,
  actionTarget,
  emptyStateText,
  managerFilterLabel,
  rowTarget,
  showingText,
} from "../../utils/helper/auctionList";
import { ResultsPager } from "../../CommonComponent/Results/ResultsPager";
import { Loader } from "rsuite";
import '../../index.css'
import introJs from 'intro.js';
import 'intro.js/introjs.css';
import { AuctionPassDialog } from "./AuctionPassDialog";
import { auctionListColumns } from "./auctionListColumns";
import { useAuctionList } from "./useAuctionList";

const TOUR_STEPS = [
  {
    element: '#auction',
    intro: 'This section showcases both ongoing and upcoming auctions where players are available for bidding.',
    position: 'right'
  },
  {
    element: '#manager-select',
    intro: 'Use this filter to show the auctions of some of your managers, or of all of them.',
    position: 'left'
  },
  {
    element: '.search-auction',
    intro: 'To subscribe to new managers, open Managers in the menu. You get access to a manager’s auctions once they approve your request.',
    position: 'right'
  },
  {
    element: '#auction-table',
    intro: 'This list displays each auction: its status, manager, start time, teams and total pool. Click a live or upcoming auction to open its details; use the button in the Action column to unlock an auction, bid or view its results.',
    position: 'top'
  },
  {
    element: '.place-bid-btn', // the blue "Place Bid" button
    intro: `
            When an auction becomes active, a <strong style="color: #fff;">blue "Place Bid"</strong> button will appear.<br><br>
            It will look like this:<br>
            <button style="
              background-color: #043bc7;
              color: white;
              padding: 6px 12px;
              border: none;
              border-radius: 4px;
              font-weight: bold;
              cursor: default;
              margin-top: 8px;
            ">
              Place Bid
            </button><br><br>
            You can click this button to place your bid during live auctions.
          `,
    position: 'bottom'
  }
];

/**
 * The player's Auctions list (#236, PL-019.1): one row per auction of the managers the player is
 * subscribed to, on a Live and Upcoming tab and a Closed tab, searched and filtered by the server.
 * A Live or Upcoming row opens Auction Details (rowTarget, #237); a closed row is not clickable.
 * Place Bid opens Auction Details too, View Results opens Auction Results (actionTarget).
 */
function AuctionPlayer() {
  const [data, setData] = useState(false);
  // Kept so the list can warn a lapsed player before they try to bid.
  const [currentSubscription, setCurrentSubscription] = useState(null);
  // The row whose auction the player is unlocking (single-auction access).
  const [auctionPassRow, setAuctionPassRow] = useState(null);
  const list = useAuctionList();
  const navigate = useNavigate();
  const isUser = JSON.parse(localStorage.getItem("isUser"));
  const loginInstruction = isUser?.isUserInstruction;
  const instructionStatus = localStorage.getItem("instruction");
  const scrolledForRef = useRef(null);

  useEffect(() => {
    GetSubScription().then((response) =>
      setCurrentSubscription(pickCurrentSubscription(response?.data?.data))
    );
  }, []);

  const actionTourStarted = useRef(false);
  useEffect(() => {
    if ((!actionTourStarted.current && loginInstruction === true) || instructionStatus === 'true') {
      introJs().setOptions({
        steps: TOUR_STEPS,
        showBullets: false,
        nextLabel: 'Next',
        prevLabel: 'Previous',
        doneLabel: 'Finish',
        exitOnOverlayClick: false,
        skipLabel: '',
        tooltipClass: 'custom-intro-tooltip',
      }).onexit(() => {
        // Runs on Finish as well as on close: intro.js exits after oncomplete.
        endUserInstructions();
      }).start();
      actionTourStarted.current = true;
    }
  }, [instructionStatus, loginInstruction]);

  const sidebarfun = () => {
    setData(!data);
  };

  // Every row carries the same account-level credit balance, so the first row that has it is as
  // good as any.
  const availableAuctionPassCredits =
    list.rows.find((item) => item?.availableAuctionPassCredits != null)
      ?.availableAuctionPassCredits ?? 0;

  const openAction = (row) => {
    const target = actionTarget(row);
    if (target) navigate(target.path, { state: target.state });
  };

  const openRow = (row) => {
    const target = rowTarget(row);
    if (target) navigate(target.path);
  };

  const columns = auctionListColumns({ onOpen: openAction, onUnlock: setAuctionPassRow });

  // #196: once per arrival from a start reminder (useAuctionList), when its auction is on screen.
  const reminder = list.reminder;
  useEffect(() => {
    if (!reminder || list.loading || scrolledForRef.current === reminder.arrival) return;

    // RDT renders each row as <div id="row-{keyField}">, and keyField is auctionId here.
    const element = document.getElementById(`row-${reminder.auctionId}`);
    if (!element) return;

    scrolledForRef.current = reminder.arrival;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [reminder, list.loading, list.rows]);

  const rowStyles = useMemo(
    () => [
      { when: (row) => rowTarget(row) !== null, classNames: ["auction-list-row-link"] },
      { when: (row) => row?.auctionId === reminder?.auctionId, classNames: ["auction-list-row-reminder"] },
    ],
    [reminder]
  );

  const empty = emptyStateText({ narrowed: list.narrowed });

  return (
    <>
      <TopNavbar sidebarfun={sidebarfun} />
      <div className="dasboard-group">
        <Sidebar data={data} />
        <div
          className={
            data ? "dashboard-content full-contnet" : "dashboard-content"
          }
        >
          <div className="auction-list">
            <h1 className="heading-dash" id="auction">Auction</h1>

            {/* Non-blocking notice. Each row carries its own canBid, so the buttons below are
                gated individually; this only explains the situation and points at the two ways
                out. */}
            {!isServerSubscriptionActive(currentSubscription) && (
              <div className="alert alert-warning" role="alert">
                {currentSubscription?.isPaymentFailed
                  ? "Your renewal payment failed, so bidding is paused. "
                  : "You are not subscribed or your subscription expired. "}
                <a href="/Player/Account_settings" className="subscribe-link">
                  Subscribe now
                </a>{" "}
                to bid in these auctions, or unlock a single auction that has not started yet
                from the table below.
                {availableAuctionPassCredits > 0 && (
                  <>
                    {" "}
                    You have {availableAuctionPassCredits} credit
                    {availableAuctionPassCredits === 1 ? "" : "s"} from a cancelled auction to
                    apply.
                  </>
                )}
              </div>
            )}

            <div className="auction-list-card" id="auction-table">
              <div className="auction-list-toolbar">
                <Nav
                  variant="tabs"
                  role="tablist"
                  className="auction-list-tabs"
                  activeKey={list.tab}
                  onSelect={list.selectTab}
                >
                  {[AUCTION_TABS.LIVE, AUCTION_TABS.CLOSED].map((key) => (
                    <Nav.Item key={key}>
                      <Nav.Link eventKey={key} role="tab" aria-selected={list.tab === key}>
                        {AUCTION_TAB_LABELS[key]}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>

                <div className="auction-list-filters">
                  <label className="auction-list-search">
                    <icons.Search aria-hidden="true" />
                    <input
                      type="search"
                      placeholder="Search auctions..."
                      aria-label="Search auctions"
                      value={list.searchText}
                      onChange={(event) => list.setSearchText(event.target.value)}
                    />
                  </label>
                  <div className="auction-list-managers" id="manager-select">
                    <MultiSelect
                      options={list.managers || []}
                      value={list.selectedManagers}
                      onChange={list.selectManagers}
                      isLoading={list.managers === null}
                      labelledBy="Filter by manager"
                      valueRenderer={(selected, options) => managerFilterLabel(selected, options)}
                      overrideStrings={{
                        selectAll: "All managers",
                        search: "Search managers",
                        noOptions: "No managers",
                      }}
                      className="auction-list-manager-select"
                    />
                  </div>
                </div>
              </div>

              <div className="auction-list-table">
                <DataTable
                  columns={columns}
                  data={list.rows}
                  keyField="auctionId"
                  onRowClicked={openRow}
                  conditionalRowStyles={rowStyles}
                  persistTableHead
                  progressPending={list.loading}
                  progressComponent={<div className="auction-list-loading"><Loader /></div>}
                  noDataComponent={
                    <div className="auction-list-empty">
                      <b>{empty.title}</b>
                      {empty.hint}
                    </div>
                  }
                />
              </div>

              <div className="auction-list-foot">
                <span>{showingText(list.totalRecords)}</span>
              </div>
              <ResultsPager
                page={list.page}
                totalPages={list.totalPages ?? 0}
                perPage={AUCTIONS_PAGE_SIZE}
                pageSizes={[AUCTIONS_PAGE_SIZE]}
                onPerPageChange={() => {}}
                onPrevious={list.previousPage}
                onNext={list.nextPage}
              />
            </div>
          </div>
        </div>
        <Footer />
      </div>

      <AuctionPassDialog
        row={auctionPassRow}
        onClose={() => setAuctionPassRow(null)}
        onRedeemed={list.reload}
      />
    </>
  );
}

export default AuctionPlayer;
